import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Descriptions,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { adminApi } from '@/services/api';
import { apiErrorMessage } from '@/utils/apiError';
import { ChallengeEditor } from './ChallengeEditor';
import {
  OBJECTIVE_LABELS,
  istDateOf,
  objectiveText,
  type Challenge,
  type ChallengeStats,
} from './challengeForm';

const STATUS_COLOR: Record<Challenge['status'], string> = {
  draft: 'default',
  published: 'green',
  archived: 'red',
};

const todayIst = () => istDateOf(new Date().toISOString());

/** Schedule: one challenge per IST day; create, edit, publish, archive, copy to the next day, stats. */
export const ScheduleTab: React.FC = () => {
  const [rows, setRows] = useState<Challenge[]>([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<string | undefined>();
  const [editing, setEditing] = useState<Challenge | null | undefined>(undefined);
  const [stats, setStats] = useState<{ c: Challenge; s: ChallengeStats } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows((await adminApi.challengerList(status ? { status } : {})).data);
    } catch (e) {
      message.error(apiErrorMessage(e, 'Could not load challenges'));
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (fn: () => Promise<unknown>, done: string) => {
    try {
      await fn();
      message.success(done);
      load();
    } catch (e) {
      message.error(apiErrorMessage(e, 'Action failed'));
    }
  };

  const showStats = async (c: Challenge) => {
    try {
      setStats({ c, s: (await adminApi.challengerStats(c.id)).data });
    } catch (e) {
      message.error(apiErrorMessage(e, 'Could not load stats'));
    }
  };

  const today = todayIst();
  const nextFreeDay = () => {
    const taken = new Set(
      rows.filter((r) => r.status !== 'archived').map((r) => istDateOf(r.startAt))
    );
    const d = new Date(`${today}T00:00:00Z`);
    while (taken.has(d.toISOString().slice(0, 10))) d.setUTCDate(d.getUTCDate() + 1);
    return d.toISOString().slice(0, 10);
  };

  const columns: ColumnsType<Challenge> = [
    {
      title: 'IST day',
      key: 'day',
      render: (_, c) => {
        const d = istDateOf(c.startAt);
        return d === today ? <Tag color="blue">{d} (today)</Tag> : d;
      },
    },
    {
      title: 'Name',
      dataIndex: 'name',
      render: (n, c) => <a onClick={() => setEditing(c)}>{n}</a>,
    },
    {
      title: 'Objective',
      key: 'objective',
      render: (_, c) => (
        <Space direction="vertical" size={0}>
          <Typography.Text>{objectiveText(c.objective, c.scenario)}</Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {OBJECTIVE_LABELS[c.objective.type]}
            {c.scenario.innings === 2
              ? ` · ${c.scenario.startingScore}/${c.scenario.wicketsLost} chasing ${c.scenario.targetScore}`
              : ''}
          </Typography.Text>
        </Space>
      ),
    },
    { title: 'Difficulty', dataIndex: 'difficulty' },
    { title: 'Rollout', dataIndex: 'rolloutPct', render: (p) => `${p}%` },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (s: Challenge['status'], c) => (
        <Tag color={STATUS_COLOR[s]}>
          {s} v{c.version}
        </Tag>
      ),
    },
    {
      title: '',
      key: 'actions',
      render: (_, c) => (
        <Space wrap>
          {c.status !== 'published' && (
            <Button
              size="small"
              type="primary"
              onClick={() => act(() => adminApi.challengerPublish(c.id), 'Published')}
            >
              Publish
            </Button>
          )}
          {c.status === 'published' && (
            <Popconfirm
              title="Archive it? Players stop seeing it at once."
              onConfirm={() => act(() => adminApi.challengerArchive(c.id), 'Archived')}
            >
              <Button size="small" danger>
                Archive
              </Button>
            </Popconfirm>
          )}
          <Button
            size="small"
            onClick={() =>
              act(() => adminApi.challengerDuplicate(c.id), 'Copied to the next day as a draft')
            }
          >
            Copy to next day
          </Button>
          <Button size="small" onClick={() => showStats(c)}>
            Stats
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Space style={{ marginBottom: 12 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setEditing(null)}>
          New challenge
        </Button>
        <Select
          allowClear
          placeholder="All statuses"
          style={{ width: 160 }}
          value={status}
          onChange={setStatus}
          options={['draft', 'published', 'archived'].map((s) => ({ value: s, label: s }))}
        />
        <Button icon={<ReloadOutlined />} onClick={load} />
      </Space>
      <Table
        rowKey="id"
        size="middle"
        loading={loading}
        dataSource={rows}
        columns={columns}
        pagination={{ pageSize: 30 }}
      />
      <ChallengeEditor
        open={editing !== undefined}
        challenge={editing ?? null}
        defaultDate={nextFreeDay()}
        onClose={() => setEditing(undefined)}
        onSaved={() => {
          setEditing(undefined);
          load();
        }}
      />
      <Modal
        open={!!stats}
        title={stats ? `Stats: ${stats.c.name} (${istDateOf(stats.c.startAt)})` : ''}
        footer={null}
        onCancel={() => setStats(null)}
      >
        {stats && (
          <>
            <Descriptions column={2} size="small" bordered>
              <Descriptions.Item label="Players started">
                {stats.s.playersStarted}
              </Descriptions.Item>
              <Descriptions.Item label="Success rate">
                {(stats.s.successRate * 100).toFixed(1)}%
              </Descriptions.Item>
              <Descriptions.Item label="Completed">{stats.s.completed}</Descriptions.Item>
              <Descriptions.Item label="Failed (no retry left)">{stats.s.failed}</Descriptions.Item>
              <Descriptions.Item label="Ad retries">{stats.s.adRetries}</Descriptions.Item>
              <Descriptions.Item label="Client disagreed">{stats.s.mismatches}</Descriptions.Item>
              <Descriptions.Item label="Flagged attempts" span={2}>
                {stats.s.flagged} (late, balls after the end, or a wicket on a wide)
              </Descriptions.Item>
            </Descriptions>
            <Table
              style={{ marginTop: 12 }}
              size="small"
              rowKey="attemptNo"
              pagination={false}
              dataSource={stats.s.attemptsByNo}
              columns={[
                { title: 'Attempt', dataIndex: 'attemptNo' },
                { title: 'Attempts', dataIndex: 'attempts' },
                { title: 'Won', dataIndex: 'succeeded' },
              ]}
            />
          </>
        )}
      </Modal>
    </>
  );
};
